import React, { useState } from "react";
import { toast } from "react-toastify";
import useInvoiceReview from "./useInvoiceReview.js";
import InvoiceReviewFilters from "./invoiceReviewFilters.jsx";
import InvoiceReviewTable from "./invoiceReviewTable.jsx";
import BulkActionDialog from "./bulkActionDialog.jsx";
import "./invoiceReview.scss";

const InvoiceReviewInvoicesPage = () => {
    const {
        items,
        nextToken,
        isLoading,
        isError,
        filters,
        setFilters,
        applyFilters,
        clearFilters,
        pageSize,
        onPageSizeChange,
        tokenStack,
        goNext,
        goPrevious,
        selectedIds,
        toggleRowSelection,
        toggleAllSelection,
        clearSelection,
        actionMutation,
        bulkActionMutation,
        canBulkAccept,
        canBulkReject,
        isFetching,
        refetch,
    } = useInvoiceReview();

    const [bulkDialog, setBulkDialog] = useState(null);

    if (isError) {
        toast.error("Service unavailable.");
    }

    const handleAccept = (item) => {
        actionMutation.mutate({ sk: item.sk, accountHccId: item.accountHccId, status: "accepted" });
    };

    const handleReject = (item) => {
        actionMutation.mutate({ sk: item.sk, accountHccId: item.accountHccId, status: "rejected" });
    };

    const selectedInvoices = bulkDialog
        ? items.filter((item) => selectedIds.has(item.sk))
        : [];

    const handleBulkConfirm = ({ sks, status }) => {
        bulkActionMutation.mutate({ sks, status });
        setBulkDialog(null);
    };

    return (
        <div className="container invoice-review">
            <p className="title">Review Invoices</p>
            <p className="sub-title">HRP-generated invoices pending approval.</p>

            <InvoiceReviewFilters
                filters={filters}
                setFilters={setFilters}
                onApply={applyFilters}
                onClear={clearFilters}
                onRefresh={refetch}
                isRefreshing={isFetching}
            />

            {selectedIds.size > 0 && (
                <div className="invoice-review__bulk-bar" role="region" aria-label="Bulk actions">
                    <span className="invoice-review__bulk-count" role="status" aria-atomic="true">
                        <span className="invoice-review__bulk-count-badge" aria-hidden="true">{selectedIds.size}</span>
                        {selectedIds.size === 1 ? "invoice selected" : "invoices selected"}
                    </span>
                    <button
                        type="button"
                        className="invoice-review__bulk-clear"
                        onClick={clearSelection}
                        aria-label="Clear selection"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className="invoice-review__bulk-btn invoice-review__bulk-btn--accept"
                        disabled={!canBulkAccept}
                        title={!canBulkAccept ? "Cannot bulk accept: selection includes accepted invoices" : undefined}
                        onClick={() => setBulkDialog("accept")}
                    >
                        Bulk Accept
                    </button>
                    <button
                        type="button"
                        className="invoice-review__bulk-btn invoice-review__bulk-btn--reject"
                        disabled={!canBulkReject}
                        title={!canBulkReject ? "Can only bulk reject invoices that are in review" : undefined}
                        onClick={() => setBulkDialog("reject")}
                    >
                        Bulk Reject
                    </button>
                </div>
            )}

            <InvoiceReviewTable
                items={items}
                isLoading={isLoading}
                selectedIds={selectedIds}
                onToggleRow={toggleRowSelection}
                onToggleAll={toggleAllSelection}
                onAccept={handleAccept}
                onReject={handleReject}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                nextToken={nextToken}
                tokenStack={tokenStack}
                onNext={goNext}
                onPrevious={goPrevious}
            />

            {bulkDialog && (
                <BulkActionDialog
                    selectedInvoices={selectedInvoices}
                    action={bulkDialog}
                    onConfirm={handleBulkConfirm}
                    onCancel={() => setBulkDialog(null)}
                />
            )}
        </div>
    );
};

export default InvoiceReviewInvoicesPage;
